import * as React from "react";
import { useMutation, useQuery } from "react-query";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import { ApplicationSummary, ApplicationsService } from "gen/clients";
import { Button, Drawer, FormControl, FormHelperText, Stack } from "@mui/material";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { CheckboxWithLabel } from "formik-mui";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";

interface Props {
  applicationSummary: ApplicationSummary;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EditApplicationPanel: React.FC<Props> = ({ applicationSummary, open, onClose, onSuccess }) => {
  const { data: application, isLoading: isApplicationLoading } = useQuery(["getApplication", applicationSummary.id], {
    queryFn: () => ApplicationsService.getApplication({ id: applicationSummary.id }),
    cacheTime: 0
  });

  const { mutate, error } = useMutation("updateApplication", {
    onSuccess,
    mutationFn: ApplicationsService.updateApplication
  });

  const validationSchema = Yup.object().shape({
    id: Yup.string().required("Required"),
    name: Yup.string().required("Required")
  });

  const onSubmit = React.useCallback(
    formValues => {
      mutate({
        id: applicationSummary.id,
        requestBody: {
          id: applicationSummary.id,
          name: formValues.name,
          description: formValues.description,
          isStandard: formValues.isStandard,
          settings: application?.settings,
          settingPresets: application?.settingPresets
        }
      });
    },
    [mutate, application, applicationSummary]
  );

  return (
    <Drawer open={open} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 800 } }}>
      <DrawerHeader title="Edit Application" onClose={onClose} />
      <DrawerBody isLoading={isApplicationLoading}>
        <Formik
          initialValues={{
            id: applicationSummary.id,
            name: applicationSummary.name,
            description: applicationSummary.description || "",
            isStandard: applicationSummary.isStandard
          }}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting }) => (
            <Form noValidate={true}>
              <Stack spacing={3}>
                <TextInputField name="id" label="Application ID" required={true} disabled={true} />
                <TextInputField name="name" label="Application Name" required={true} />
                <TextInputField name="description" label="Description" />
                <FormControl>
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    name="isStandard"
                    Label={{
                      label: "Standard application"
                    }}
                  />
                  <FormHelperText>Standard applications are displayed to all clients</FormHelperText>
                </FormControl>
              </Stack>

              <Stack spacing={1} direction="row" marginTop={4}>
                {!!error && <ApiErrorMessage apiError={error} />}
                <LoadingButton isLoading={isSubmitting}>Apply</LoadingButton>
                <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </DrawerBody>
    </Drawer>
  );
};

export { EditApplicationPanel };
